import { useCallback, useState } from "react"
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
} from "@mui/material"
import { useDialogFormState } from "../../hooks/use-dialog-form-state"

export interface RenameDashboardDialogProps {
  open: boolean
  // The dashboard's current name — the field is prefilled with it so the user edits in place.
  currentName: string
  onClose: () => void
  // Resolves → dialog closes; rejects → dialog stays open with the inline error.
  onRename: (name: string) => Promise<void>
}

export default function RenameDashboardDialog({
  open,
  currentName,
  onClose,
  onRename,
}: RenameDashboardDialogProps) {
  // Seeded at mount; the parent remounts per open, so no prop→state sync effect (avoids set-state-in-effect).
  const [name, setName] = useState(currentName)
  const { isBusy: isSaving, error, beginBusy, endBusy, guardedClose } = useDialogFormState()

  const handleClose = useCallback(() => {
    guardedClose(onClose)
  }, [guardedClose, onClose])

  const trimmedName = name.trim()
  // Save is disabled when empty, unchanged, or a request is in flight.
  const isSaveDisabled = trimmedName.length === 0 || trimmedName === currentName.trim() || isSaving

  const handleSave = useCallback(async () => {
    if (trimmedName.length === 0 || trimmedName === currentName.trim()) return
    if (!beginBusy()) return
    try {
      await onRename(trimmedName)
      onClose()
    } catch {
      // Keep the dialog open so the user can retry without re-typing.
      endBusy("Couldn't rename the dashboard. Please try again.")
    }
  }, [trimmedName, currentName, beginBusy, onRename, onClose, endBusy])

  return (
    <Dialog open={open} onClose={handleClose} fullWidth maxWidth="xs">
      <DialogTitle>Rename dashboard</DialogTitle>
      <DialogContent>
        <TextField
          autoFocus
          required
          fullWidth
          margin="dense"
          label="Dashboard name"
          value={name}
          onChange={(event) => { setName(event.target.value) }}
          error={error !== null}
          helperText={error ?? undefined}
          disabled={isSaving}
          onKeyDown={(event) => {
            if (event.key === "Enter") {
              event.preventDefault()
              void handleSave()
            }
          }}
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={isSaving}>Cancel</Button>
        <Button onClick={() => { void handleSave() }} disabled={isSaveDisabled} variant="contained">
          Save
        </Button>
      </DialogActions>
    </Dialog>
  )
}
